// useBilling hook — single source of truth for plan/usage/pricing on the frontend.
//
// Exposes:
//   pricing       — { starter, pro: { USD, INR }, key_id } from /api/billing/pricing
//   billing       — { plan, messages_used, messages_remaining, message_limit } from /api/billing/me (null if anon)
//   currency      — "USD" | "INR" detected from user locale (overridable via setCurrency)
//   refreshBilling()  — re-fetch /api/billing/me (call after a successful purchase)
//   upgrade(billingCycle)  — opens Razorpay Checkout, then refreshes billing
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  fetchPricing, fetchBillingMe, openCheckout, detectCurrency,
} from "@/lib/billing";

export function useBilling() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [billing, setBilling] = useState(null);
  const [currency, setCurrencyState] = useState(() => detectCurrency());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pricing once (it's static config)
  useEffect(() => {
    let cancelled = false;
    fetchPricing()
      .then((p) => { if (!cancelled) setPricing(p); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  // Billing depends on auth
  const refreshBilling = useCallback(async () => {
    if (!user) { setBilling(null); setLoading(false); return; }
    setLoading(true);
    try {
      const b = await fetchBillingMe();
      setBilling(b);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refreshBilling(); }, [refreshBilling]);

  const setCurrency = useCallback((c) => {
    if (c === "USD" || c === "INR") setCurrencyState(c);
  }, []);

  const upgrade = useCallback(async (billingCycle = "monthly") => {
    if (!user) throw new Error("Sign in first");
    const result = await openCheckout({
      currency,
      billingCycle,
      user,
      pricing,
    });
    await refreshBilling();
    return result;
  }, [user, currency, pricing, refreshBilling]);

  const isPro = billing?.plan === "pro";

  return {
    pricing, billing, currency, setCurrency,
    loading, error, isPro,
    refreshBilling, upgrade,
  };
}
